'use client';

/**
 * CountrySelect — the sign-up country field.
 *
 * A native <select> cannot carry an image, and the country list is the one
 * place in sign-up where a flag does real work: it is how someone confirms at
 * a glance that they picked the right one. So this is the /scan jurisdiction
 * menu's pattern (`.scan-menu` chrome, flag + label rows, copper selected
 * state) bound to a form field.
 *
 * It is a combobox, not a menu: the button owns `role="combobox"` and the
 * panel a `role="listbox"` of `role="option"` rows, so a screen reader
 * announces it as the form control it is. Keyboard support is the listbox
 * minimum — arrows, Home/End, Enter/Space, Escape — plus a filter box.
 *
 * `value` is an ISO 3166-1 alpha-2 code, or the caller's "other" sentinel, or
 * '' for unset. The empty string is a real state: it is what lets the field be
 * required without a fake default.
 */
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';

import { Flag } from '@/components/ui/Flag';

export interface CountrySelectOption {
  code: string;
  label: string;
  /** Sub-jurisdictions the corpus covers, where narrower than the country. */
  covers?: string[];
}

interface CountrySelectProps {
  value: string;
  onChange: (code: string) => void;
  options: readonly CountrySelectOption[];
  /** Sentinel emitted by the trailing "not listed" row. Omit to hide it. */
  otherValue?: string;
  otherLabel?: string;
  loading?: boolean;
  disabled?: boolean;
  /** Paints the error border — this control is outside native validation. */
  invalid?: boolean;
  id?: string;
  /** Element id holding the field's visible label, for aria-labelledby. */
  labelledBy?: string;
  placeholder?: string;
}

export function CountrySelect({
  value,
  onChange,
  options,
  otherValue,
  otherLabel = 'Other — not listed',
  loading = false,
  disabled = false,
  invalid = false,
  id,
  labelledBy,
  placeholder = 'Select your country',
}: CountrySelectProps) {
  const generatedId = useId();
  const fieldId = id ?? generatedId;
  const listId = `${fieldId}-list`;

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // The "not listed" row is an option like any other once it exists — keeping
  // it in the same array is what makes the arrow keys and the filter reach it.
  const rows = useMemo<CountrySelectOption[]>(() => {
    const base = [...options];
    if (otherValue) base.push({ code: otherValue, label: otherLabel });
    return base;
  }, [options, otherValue, otherLabel]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) => r.label.toLowerCase().includes(q) || r.code.toLowerCase().startsWith(q),
    );
  }, [rows, query]);

  const selected = rows.find((r) => r.code === value) ?? null;

  // Clamped on read rather than corrected in an effect: filtering can shrink
  // the list under a stored index at any keystroke.
  const active = visible.length ? Math.min(activeIndex, visible.length - 1) : 0;

  const close = useCallback((refocus = true) => {
    setOpen(false);
    setQuery('');
    if (refocus) buttonRef.current?.focus();
  }, []);

  const commit = useCallback(
    (code: string) => {
      onChange(code);
      close();
    },
    [onChange, close],
  );

  // Outside click. Pointerdown rather than click so the panel closes before a
  // click on another control lands on a layout that has since moved.
  useEffect(() => {
    if (!open) return undefined;
    function onPointerDown(ev: PointerEvent) {
      if (!rootRef.current?.contains(ev.target as Node)) close(false);
    }
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, close]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
  }, [open]);

  // Keep the active row in view when the arrows walk past the fold.
  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>('[data-active="true"]');
    el?.scrollIntoView({ block: 'nearest' });
  }, [open, active, query]);

  function openPanel() {
    if (disabled || loading) return;
    setActiveIndex(Math.max(0, rows.findIndex((r) => r.code === value)));
    setOpen(true);
  }

  function onKeyDown(ev: React.KeyboardEvent) {
    if (!open) {
      if (ev.key === 'ArrowDown' || ev.key === 'Enter' || ev.key === ' ') {
        ev.preventDefault();
        openPanel();
      }
      return;
    }
    switch (ev.key) {
      case 'Escape':
        ev.preventDefault();
        close();
        break;
      case 'ArrowDown':
        ev.preventDefault();
        setActiveIndex(visible.length ? (active + 1) % visible.length : 0);
        break;
      case 'ArrowUp':
        ev.preventDefault();
        setActiveIndex(visible.length ? (active - 1 + visible.length) % visible.length : 0);
        break;
      case 'Home':
        ev.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        ev.preventDefault();
        setActiveIndex(Math.max(0, visible.length - 1));
        break;
      case 'Enter': {
        ev.preventDefault();
        const row = visible[active];
        if (row) commit(row.code);
        break;
      }
      case 'Tab':
        close(false);
        break;
      default:
        break;
    }
  }

  const isOther = otherValue !== undefined && value === otherValue;

  return (
    <div ref={rootRef} className="lex-country" onKeyDown={onKeyDown}>
      <button
        ref={buttonRef}
        aria-controls={open ? listId : undefined}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-invalid={invalid || undefined}
        aria-labelledby={labelledBy ? `${labelledBy} ${fieldId}` : undefined}
        className={`input lex-country__field${open ? ' is-open' : ''}${
          invalid ? ' is-error' : ''
        }`}
        disabled={disabled || loading}
        id={fieldId}
        role="combobox"
        type="button"
        onClick={() => (open ? close() : openPanel())}
      >
        <span className="lex-country__value">
          {loading ? (
            <span className="lex-country__placeholder">Loading countries…</span>
          ) : selected ? (
            <>
              {!isOther && (
                <span aria-hidden="true" className="scan-menu-item-flag">
                  <Flag code={selected.code.toLowerCase()} size={16} />
                </span>
              )}
              <span>{selected.label}</span>
            </>
          ) : (
            <span className="lex-country__placeholder">{placeholder}</span>
          )}
        </span>
        <span aria-hidden="true" className="lex-country__caret">
          <svg fill="none" height="14" viewBox="0 0 24 24" width="14">
            <path
              d="m6 9 6 6 6-6"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.6"
            />
          </svg>
        </span>
      </button>

      {open && (
        <div className="scan-menu lex-country__menu">
          <div className="scan-menu-search">
            <svg fill="none" height="14" viewBox="0 0 24 24" width="14">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="1.6" />
              <path
                d="m20 20-3.5-3.5"
                stroke="currentColor"
                strokeLinecap="round"
                strokeWidth="1.6"
              />
            </svg>
            <input
              ref={searchRef}
              aria-label="Filter countries"
              placeholder="Search"
              type="text"
              value={query}
              onChange={(ev) => {
                setQuery(ev.target.value);
                setActiveIndex(0);
              }}
            />
          </div>

          <div ref={listRef} className="scan-menu-body" id={listId} role="listbox">
            <div className="scan-menu-label">Where your organisation is based</div>
            {visible.length === 0 ? (
              <div className="lex-country__empty">No match — pick {otherLabel}.</div>
            ) : (
              visible.map((row, i) => {
                const isSelected = row.code === value;
                const isSentinel = otherValue !== undefined && row.code === otherValue;
                return (
                  <button
                    key={row.code}
                    aria-selected={isSelected}
                    className={`scan-menu-item${isSelected ? ' is-selected' : ''}${
                      i === active ? ' is-active' : ''
                    }`}
                    data-active={i === active}
                    role="option"
                    type="button"
                    onClick={() => commit(row.code)}
                    onPointerMove={() => setActiveIndex(i)}
                  >
                    <span aria-hidden="true" className="scan-menu-item-flag">
                      {isSentinel ? (
                        <svg fill="none" height="16" viewBox="0 0 24 24" width="16">
                          <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.4" />
                          <path
                            d="M3 12h18M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z"
                            stroke="currentColor"
                            strokeWidth="1.4"
                          />
                        </svg>
                      ) : (
                        <Flag code={row.code.toLowerCase()} size={18} />
                      )}
                    </span>
                    <span className="scan-menu-item-body">
                      <span className="scan-menu-item-title">{row.label}</span>
                      {row.covers && row.covers.length > 0 && (
                        <span className="scan-menu-item-sub">{row.covers.join(' · ')}</span>
                      )}
                    </span>
                    {isSelected && (
                      <svg aria-hidden="true" fill="none" height="14" viewBox="0 0 24 24" width="14">
                        <path
                          d="m5 12.5 4.5 4.5L19 7.5"
                          stroke="currentColor"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="1.8"
                        />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CountrySelect;
